// Phase 2.1: shapes come from the codegenned api.gen.ts. Runtime arrays
// + the query-param shape stay hand-written.
import type { components } from "./api.lyse.gen.js";

export type DefectStatus = components["schemas"]["DefectStatus"];
export type DefectSeverity = components["schemas"]["DefectSeverity"];
export type Defect = components["schemas"]["Defect"];
export type GetDefectsResponse = components["schemas"]["GetDefectsResponse"];

export type UpdateDefectBody = components["schemas"]["UpdateDefectBody"];
export type UpdateDefectResponse = Defect;

// 7.22.0 (ANA-R3-GEOM-01) — geometry additions consumed by GEOM-03/04/05.
//
// `Defect.path` (read) is a flat `[x0,y0,…]` array **normalised 0..1**; closed
// iff the first/last pair match. `Defect.geometryOrigin` is provenance
// (`model` | `model_adjusted` | `manual`) but EXTENSIBLE — the gen type unions
// in `(string & {})` so an unknown future value still type-checks.
export type GeometryPathNormalised =
  components["schemas"]["GeometryPathNormalised"];
/** Provenance of a defect's geometry; `string & {}` keeps it open/extensible. */
export type DefectGeometryOrigin = NonNullable<Defect["geometryOrigin"]>;

// PATCH /api/lyse/v1/defects/{id}/geometry (operationId: updateDefectGeometry).
// Request `path` is a flat `[x0,y0,…]` polygon in **pixel** space (NOT
// normalised — symmetric with createDefectFromImage). Response is the updated
// Defect (recomputed area + re-derived bbox + normalised read `path`).
export type UpdateDefectGeometryBody =
  components["schemas"]["UpdateDefectGeometryBody"];
export type UpdateDefectGeometryResponse = Defect;

// Defect-catalogue surface (BR-083 / BR-084 / BR-085 / BR-086). Re-exported
// here so feature code consumes them via @aeriosiq/types like every other
// generated shape — the previous local `contractExt.ts` was a stopgap
// while the lyse contract caught up.
export type LocalizedString = components["schemas"]["LocalizedString"];
export type DefectClass = components["schemas"]["DefectClass"];
export type DefectClassModel = components["schemas"]["DefectClassModel"];
export type DefectCatalogue = components["schemas"]["DefectCatalogue"];
export type DefectCatalogueEntry =
  components["schemas"]["DefectCatalogueEntry"];
export type GetDefectCataloguesResponse =
  components["schemas"]["GetDefectCataloguesResponse"];
export type GetDefectCatalogueResponse =
  components["schemas"]["GetDefectCatalogueResponse"];
export type GetDefectCatalogueFavouritesResponse =
  components["schemas"]["GetDefectCatalogueFavouritesResponse"];
export type ToggleFavouriteBody = components["schemas"]["ToggleFavouriteBody"];
// UpdateMapperBody / UpdateMapperResponse: removed — the contract retired the
// updateMapper operation + schemas in 7.21.0 (AIMAP-03); the single-row
// suggestion surface lives in taxonomy/v1 (see ./taxonomy.js SuggestAiMapBody).

// ── DEFCAT epic (lyse@7.9.0) — two-axis catalogue + collections + sharing.
// FE-03 regen (2026-06-11): these shapes are now IN the generated lyse schema,
// so the former hand-written mirrors are re-pointed at
// `components["schemas"][...]`. Only narrowings the YAML doesn't encode
// (literal `axis` union) stay hand-written.

/**
 * One external-system id mapping on a catalogue entry (taxonomy@1.1.0 /
 * DEFCAT-R2-07). Array, replace-list semantics on PATCH. Provider ≤64 chars,
 * id ≤256.
 */
export type CatalogueEntryExternalId =
  components["schemas"]["CatalogueEntryExternalId"];

/**
 * DefectCatalogueEntry with the two-axis `axis` field narrowed to its literal
 * union. Since the FE-03 regen the generated entry carries ALL two-axis fields
 * (entryType, parentEntryId, componentGroupId, globalLocationDetailId,
 * abbreviation, defaultDescription, isLeaf, depth, defaultSeverityByModel,
 * externalIds, isActive, sortOrder) — only `axis` is typed `string | null` in
 * the YAML, so this narrowing is the one hand-written delta that remains.
 */
export interface DefectCatalogueEntryV2 extends DefectCatalogueEntry {
  axis?: "type" | "location" | null;
}

export interface CatalogueValidityPair {
  locationEntryId: string;
  typeEntryId: string;
  /**
   * DEFCAT-R2-12 / taxonomy@1.2.0 + lyse@7.11.0 — `true` marks this pair as an
   * EXCLUSION delta (node-local on the location axis); `false`/absent = an
   * include rule. See `taxonomy/v1/design-notes/allocation-inheritance.md`;
   * `effectiveTypeEntryIds` implements the resolve algorithm client-side.
   */
  exclusion?: boolean | null;
}

export interface CatalogueAxisLevel {
  axis: "type" | "location";
  level: number;
  label: LocalizedString;
}

/** GET /defect-catalogues/{id}?axis=&includeValidity= */
export interface GetDefectCatalogueV2Response {
  catalogueId: string;
  entries: DefectCatalogueEntryV2[];
  validity?: CatalogueValidityPair[] | null;
  axisLevels?: CatalogueAxisLevel[] | null;
}

// Collections + cross-tenant sharing (DEFCAT P4/P7) — generated since the
// FE-03 regen; formerly hand-written mirrors.
export type DefectCollection = components["schemas"]["DefectCollection"];
export type CollectionVisibility = DefectCollection["visibility"];
export type ListDefectCollectionsResponse =
  components["schemas"]["ListDefectCollectionsResponse"];
export type CreateDefectCollectionBody =
  components["schemas"]["CreateDefectCollectionBody"];
export type UpdateDefectCollectionBody =
  components["schemas"]["UpdateDefectCollectionBody"];
export type SetActiveCollectionBody =
  components["schemas"]["SetActiveCollectionBody"];
export type SetActiveCollectionResponse =
  components["schemas"]["SetActiveCollectionResponse"];

/** GET /defect-catalogues/shared (tier-2 cross-tenant). */
export type SharedCatalogue = components["schemas"]["SharedCatalogue"];
export type ListSharedCataloguesResponse =
  components["schemas"]["ListSharedCataloguesResponse"];

/** Drawer validation toggle (FIX-3) + two-axis defect-create fields (additive). */
export type DefectValidation = "definitely" | "suspicion";

// Runtime constants — used by the FE filter controls + chip pickers.
// DRIFT FIX (2026-06-05): `reviewed` was added to the contract enum in lyse
// 5.2.0 (between `detected` and `suspected`) but never to this constant, so
// the Defects status filter could not show/select it (live data has
// `reviewed` rows). Order mirrors the contract enum.
export const DEFECT_STATUSES: readonly DefectStatus[] = [
  "detected",
  "reviewed",
  "suspected",
  "confirmed",
  "repaired",
] as const;

export const DEFECT_SEVERITIES: readonly DefectSeverity[] = [
  "info",
  "low",
  "medium",
  "high",
  "critical",
] as const;

// Query-param shape for /inspections/{id}/defects. NOT in the YAML
// schemas section, so kept hand-written.
//
// 5.6.0 (R58-L-07) — two additive optional params: opaque `cursor`
// (advance pagination) and `imageId` (scope to current image).
// 5.8.0 (R58-L-05) — additive `q` text-search param. AND-composes with
// every other filter; trimmed, case-insensitive ILIKE server-side
// against the joined catalogue-entry name in DE or EN.
export interface GetDefectsParams {
  page?: number | undefined;
  pageSize?: number | undefined;
  status?: DefectStatus[] | undefined;
  cursor?: string | undefined;
  imageId?: string | undefined;
  q?: string | undefined;
  /**
   * 5.8.0 (R58-L-05) — Track C.4 — filter by selected defect-class ids.
   * AND-composes with the rest of the filter set. The BE returns
   * defects whose `catalogue_entry_id` (or `defect_class_id`) is in
   * the provided list.
   */
  defectClassIds?: string[] | undefined;
}
